<template>
  <nav class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700" aria-label="Main navigation">
    <div class="px-6">
      <div class="flex justify-between items-center h-16">
        <!-- Left side - Logo -->
        <div class="flex items-center gap-4 flex-shrink-0">
          <!-- Kaizen Platform logo always visible -->
          <router-link to="/" class="flex items-center gap-3 no-underline hover:no-underline">
            <!-- Favicon icon -->
            <img 
              src="/favicon.png" 
              alt="Kaizen" 
              class="object-contain"
              style="width: 40px; height: 40px;"
            />
            <!-- Kaizen text - hidden when on project page -->
            <span v-if="!props.projectName" class="text-2xl font-semibold text-gray-900 dark:text-white">Kaizen</span>
          </router-link>
          <!-- Show project name if provided -->
          <span v-if="props.projectName" class="text-lg font-semibold text-gray-900 dark:text-white truncate ml-1 pl-1 border-l border-gray-300 dark:border-gray-600">
            {{ props.projectName }}
          </span>
        </div>

        <!-- Right side - Theme toggle and User menu -->
        <div class="flex items-center gap-4">
          <!-- Theme toggle -->
          <HLButton
            v-if="false"
            @click="toggleTheme"
            variant="text"
            color="gray"
            size="md"
            icon-only
            :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          >
            <template #iconLeft>
              <Icon v-if="isDark" name="sun" size="md" />
              <Icon v-else name="moon" size="md" />
            </template>
          </HLButton>

          <!-- Where you're mentioned (assigned / tagged) - notifications list -->
          <div class="relative notification-bell-wrap" ref="mentionsMenuRef">
            <HLButton
              @click="toggleMentions"
              variant="text"
              color="gray"
              size="sm"
              class="relative notification-bell-btn !p-1"
              :class="{ 'notification-bell-open': showMentions }"
              :title="t('topNav.whereYouAreMentioned')"
            >
              <template #iconLeft>
                <span class="relative inline-flex flex-shrink-0">
                  <Icon name="bell" size="md" class="notification-bell-icon" />
                  <HLBadge v-if="assignedNotificationsUnreadCount > 0" color="red" size="sm" class="absolute top-0 right-0 notification-badge">
                    {{ assignedNotificationsUnreadCount }}
                  </HLBadge>
                </span>
              </template>
            </HLButton>

            <!-- Notifications dropdown: read/unread, mark as read -->
            <div
              v-if="showMentions"
              class="mentions-dropdown origin-top-right absolute right-0 mt-2 w-[28rem] max-w-[calc(100vw-2rem)] rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none z-50"
              style="min-width: 360px; max-height: 70vh;"
            >
              <div class="px-3 py-2.5 border-b border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between gap-1.5">
                  <div class="min-w-0">
                    <Heading level="h4" class="text-gray-900 dark:text-white !text-base !font-semibold !leading-tight !m-0">{{ t('topNav.whereYouAreMentioned') }}</Heading>
                    <Text variant="body-sm" color="muted" class="mt-0.5 block !text-sm !leading-tight">{{ t('topNav.whereYouAreMentionedDescription') }}</Text>
                  </div>
                  <HLButton
                    v-if="assignedNotificationsUnreadCount > 0"
                    @click.stop="markAllAssignedAsRead()"
                    variant="text"
                    color="blue"
                    size="sm"
                    class="!py-1 !px-2 !min-w-0 flex-shrink-0"
                  >
                    {{ t('topNav.markAllAsRead') }}
                  </HLButton>
                </div>
              </div>
              <div class="max-h-64 overflow-y-auto py-1">
                <div v-if="assignedNotificationsLoading" class="px-3 py-4 text-center">
                  <Text variant="body-sm" color="muted">{{ t('common.loading') }}</Text>
                </div>
                <div v-else-if="assignedNotificationItems.length === 0" class="px-3 py-4 text-center">
                  <Text variant="body-sm" color="muted">{{ t('topNav.noAssignedItems') }}</Text>
                </div>
                <template v-else>
                  <div
                    v-for="item in assignedNotificationItems"
                    :key="`${item.type}-${item.id}`"
                    class="flex items-start gap-1.5 pl-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    :class="item.read ? 'bg-transparent' : 'bg-primary-50/50 dark:bg-primary-900/10'"
                  >
                    <div v-if="!item.read" class="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5" aria-hidden="true" />
                    <div v-else class="flex-shrink-0 w-1.5 mt-1.5" aria-hidden="true" />
                    <router-link
                      :to="item.to"
                      class="flex-1 flex items-start gap-1.5 py-2 pr-3 pl-0 text-xs no-underline min-w-0"
                      :class="item.read ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'"
                      @click="markAssignedItemAsRead(item); closeMentions()"
                    >
                      <Icon :name="getNotificationItemIcon(item.type)" size="sm" class="text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                      <span class="flex-1 min-w-0">
                        <span :class="item.read ? 'font-normal' : 'font-medium'">{{ item.selfAssigned ? t('topNav.youHaveCreatedTask', { id: item.displayId }) : t('topNav.youHaveBeenTaggedUnder', { id: item.displayId }) }}</span>
                        <span v-if="item.title" class="block truncate text-gray-500 dark:text-gray-400 mt-0.5 text-[11px] leading-tight" :title="item.title">{{ item.title }}</span>
                      </span>
                      <Icon name="chevronRight" size="xs" class="text-gray-400 flex-shrink-0 mt-0.5" />
                    </router-link>
                  </div>
                </template>
              </div>
              <div class="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700">
                <router-link
                  to="/assigned-to-me"
                  class="block text-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 no-underline py-1"
                  @click="closeMentions"
                >
                  {{ t('topNav.viewAssignedToMe') }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Notifications - Hidden as per requirements -->
          <div v-if="false" class="relative" ref="notificationMenuRef">
            <HLButton
              @click="toggleNotifications"
              variant="text"
              color="gray"
              size="sm"
              class="relative"
              title="Notifications"
            >
              <template #iconLeft>
                <Icon name="bell" size="md" />
              </template>
              <HLBadge v-if="notificationCount > 0" color="red" size="sm" class="absolute -top-1 -right-1">
                {{ notificationCount }}
              </HLBadge>
            </HLButton>

            <!-- Notifications dropdown menu -->
            <div
              v-if="showNotifications"
              class="notification-dropdown origin-top-right absolute right-0 mt-2 w-80 max-w-sm rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
              style="min-width: 300px;"
            >
              <div class="px-4 py-2 border-b border-gray-100">
                <div class="flex items-center justify-between">
                  <Heading level="h4">{{ t('topNav.notifications') }}</Heading>
                  <HLButton
                    v-if="notificationCount > 0"
                    @click="markAllAsRead"
                    variant="text"
                    color="blue"
                    size="sm"
                  >
                    {{ t('topNav.markAllAsRead') }}
                  </HLButton>
                </div>
              </div>
              
              <div class="max-h-64 overflow-y-auto">
                <div v-if="notifications.length === 0" class="px-4 py-3 text-center">
                  <Text variant="body-sm" color="muted">{{ t('topNav.noNotifications') }}</Text>
                </div>
                
                <div
                  v-for="notification in notifications"
                  :key="notification.id"
                  class="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  :class="{ [NOTIFICATION_COLORS.unreadBg]: !notification.read }"
                >
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div :class="['h-2 w-2 rounded-full mt-2', NOTIFICATION_COLORS.unreadDot]" v-if="!notification.read"></div>
                    </div>
                    <div class="ml-3 flex-1">
                      <Text variant="label">{{ notification.title }}</Text>
                      <Text variant="body-sm" color="muted" class="mt-1">{{ notification.message }}</Text>
                      <Text variant="caption" color="muted" class="mt-1">{{ formatTime(notification.timestamp) }}</Text>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="px-4 py-2 border-t border-gray-100">
                <router-link
                  to="/notifications"
                  :class="['block text-center text-sm', NOTIFICATION_COLORS.link]"
                  @click="closeNotifications"
                >
                  {{ t('topNav.viewAllNotifications') }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- AI Test Case Generator / Chrome Extension (icon only, circle boundary) -->
          <router-link
            to="/chrome-extension"
            class="chrome-extension-nav-link flex items-center justify-center flex-shrink-0"
            :title="t('topNav.aiTestCaseGenerator')"
          >
            <HLButton
              variant="text"
              color="gray"
              size="sm"
              icon-only
              class="chrome-extension-btn !rounded-full !w-8 !h-8 !min-w-0 !p-0 !m-0"
            >
              <template #iconLeft>
                <img
                  src="/ai-test-case-generator.png"
                  alt="AI Test Case Generator"
                  class="chrome-extension-nav-img"
                />
              </template>
            </HLButton>
          </router-link>

          <!-- User menu -->
          <HLDropdown
            id="user-menu-dropdown"
            v-model:show="showUserMenu"
            :options="userMenuOptions"
            trigger="click"
            placement="bottom-end"
            :width="280"
            :show-search="false"
            @select="handleUserMenuSelect"
          >
            <template #default>
              <HLButton
                variant="text"
                color="gray"
                size="md"
                aria-expanded="showUserMenu"
                aria-haspopup="true"
                class="!px-2 !py-1.5"
                @click="showUserMenu = !showUserMenu"
              >
                <span class="sr-only">Open user menu</span>
                <div class="flex items-center gap-2">
                  <HLAvatar
                    size="sm"
                    :name="user?.name || 'User'"
                    color="primary"
                  />
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-200 hidden md:block">
                    {{ user?.name || 'User' }}
                  </span>
                  <Icon name="chevronDown" class="w-4 h-4 text-gray-500 dark:text-gray-400 hidden md:block" />
                </div>
              </HLButton>
            </template>
          </HLDropdown>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, h, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { usePermissions } from '@/composables/usePermissions'
import { useAssignedNotifications } from '@/composables/useAssignedNotifications'
import { USER_ROLE_LABELS, USER_ROLES } from '@/constants/common'
import { HLButton, HLBadge, HLDropdown, HLAvatar, HLTag, HLDivider } from '@platform-ui/highrise'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import Icon from '@/components/ui/Icon.vue'
import { NOTIFICATION_COLORS } from '@/constants/uiColors'
import { t } from '@/i18n/en-us'

// Props
const props = defineProps({
  projectName: {
    type: String,
    default: null
  }
})

// Emits
const emit = defineEmits(['toggle-sidebar'])

// Stores
const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
const { can } = usePermissions()
const {
  items: assignedNotificationItems,
  loading: assignedNotificationsLoading,
  unreadCount: assignedNotificationsUnreadCount,
  fetchAssignedItems,
  removeItem: removeAssignedItem,
  markAsRead: markAssignedItemAsRead,
  markAllAsRead: markAllAssignedAsRead
} = useAssignedNotifications()

// Reactive data
const showMentions = ref(false)
const mentionsMenuRef = ref(null)
const showNotifications = ref(false)
const showUserMenu = ref(false)
const notificationMenuRef = ref(null)
const notificationCount = ref(0)
const notifications = ref([
  {
    id: 1,
    title: 'New Test Case Created',
    message: 'A new test case "User Login Test" has been created for E-Commerce Platform',
    timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    read: false
  },
  {
    id: 2,
    title: 'Bug Reported',
    message: 'A new bug "Add to Cart Button Not Working" has been reported',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    read: false
  },
  {
    id: 3,
    title: 'Test Run Completed',
    message: 'Sprint 1 Regression Test has been completed successfully',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    read: true
  }
])

function getNotificationItemIcon(type) {
  const icons = { bug: 'bug', feature: 'feature', accessibility: 'checkCircle', 'test-case': 'testCase' }
  return icons[type] || 'tag'
}

// Computed
const user = computed(() => authStore.user)
const isDark = computed(() => themeStore.isDark)
const canManageUsers = computed(() => can.viewUsers())
const canManageTeams = computed(() => can.viewTeams())

// Helper function for role tag color and styling
const getRoleTagColor = (role) => {
  const colorMap = {
    [USER_ROLES.SUPER_ADMIN]: 'purple',
    [USER_ROLES.ADMIN]: 'warning', // orange -> warning for HLTag compatibility
    [USER_ROLES.PROJECT_MANAGER]: 'blue',
    [USER_ROLES.TESTER]: 'success', // green -> success for HLTag compatibility
    [USER_ROLES.DEVELOPER]: 'info', // cyan -> info for HLTag compatibility
    [USER_ROLES.VIEWER]: 'gray'
  }
  return colorMap[role] || colorMap[USER_ROLES.VIEWER]
}

// User menu options for HLDropdown
const userMenuOptions = computed(() => {
  const options = []
  
  // User info header with avatar using render function
  options.push({
    type: 'render',
    key: 'user-header',
    render: () => h('div', { 
      class: 'px-4 pt-4 pb-2 border-b border-gray-100 dark:border-gray-700 cursor-pointer',
      onClick: () => { showUserMenu.value = false }
    }, [
      h('div', { class: 'flex items-start gap-2' }, [
        h(HLAvatar, {
          size: "sm",
          name: user.value?.name || 'User',
          color: 'primary'
        }),
        h('div', { class: 'flex flex-col gap-0.5 flex-1 min-w-0' }, [
          h('p', { 
            class: 'm-0 truncate text-sm text-gray-900 dark:text-white' 
          }, user.value?.name || 'User'),
          h('p', { 
            class: 'm-0 truncate text-xs text-gray-600 dark:text-gray-400',
            title: user.value?.email || 'user@example.com'
          }, user.value?.email || 'user@example.com'),
          // Only show role tag if user is not a tester
          user.value?.role?.toLowerCase() !== 'tester' ? h('div', { class: 'mt-0.5' }, [
            h(HLTag, {
              size: 'xs',
              color: getRoleTagColor(user.value?.role),
              round: true,
              interactive: false,
              bordered: false
            }, {
              default: () => USER_ROLE_LABELS[user.value?.role] || user.value?.role || 'User'
            })
          ]) : null
        ])
      ])
    ])
  })
  

  // Divider
  options.push({
    type: 'render',
    key: 'divider-top',
    render: () => h('div', { class: 'border-b border-gray-100 dark:border-gray-700' })
  })

  
  // Profile Settings
  options.push({
    label: t('topNav.profileSettings'),
    key: 'profile'
  })
  
  // Team Management (conditional)
  if (canManageTeams.value) {
    options.push({
      label: t('topNav.teamManagement'),
      key: 'teams'
    })
  }
  
  // User Management (conditional)
  if (canManageUsers.value) {
    options.push({
      label: t('topNav.userManagement'),
      key: 'users'
    })
  }
  

  // Divider before sign out
  options.push({
    type: 'render',
    key: 'divider-bottom',
    render: () => h('div', { class: 'border-b border-gray-100 dark:border-gray-700' })
  })

  
  // Sign out
  options.push({
    label: t('topNav.signOut'),
    key: 'signout'
  })
  
  return options
})

// Update notification count when notifications change
const updateNotificationCount = () => {
  notificationCount.value = notifications.value.filter(n => !n.read).length
}

// Methods
const toggleTheme = () => {
  themeStore.toggleTheme()
}

const toggleMentions = () => {
  showMentions.value = !showMentions.value
  if (showMentions.value) {
    fetchAssignedItems()
    nextTick(() => {
      const dropdown = document.querySelector('.mentions-dropdown')
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        if (rect.right > viewportWidth - 10) {
          dropdown.style.right = '10px'
          dropdown.style.left = 'auto'
        }
      }
    })
  }
}

const closeMentions = () => {
  showMentions.value = false
}

const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value
  
  if (showNotifications.value) {
    // Position dropdown to prevent overflow
    nextTick(() => {
      const dropdown = document.querySelector('.notification-dropdown')
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        
        // If dropdown would overflow on the right, adjust position
        if (rect.right > viewportWidth - 10) {
          dropdown.style.right = '10px'
          dropdown.style.left = 'auto'
        }
      }
    })
  }
}

const closeNotifications = () => {
  showNotifications.value = false
}

const markAllAsRead = () => {
  notifications.value.forEach(notification => {
    notification.read = true
  })
  updateNotificationCount()
}

const formatTime = (timestamp) => {
  const now = new Date()
  const diff = now - timestamp
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  
  if (minutes < 60) {
    return `${minutes}m ago`
  } else if (hours < 24) {
    return `${hours}h ago`
  } else {
    return `${days}d ago`
  }
}

const getUserInitials = (name) => {
  if (!name) return 'U'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

// Handle user menu selection
const handleUserMenuSelect = (key) => {
  showUserMenu.value = false
  
  switch (key) {
    case 'profile':
      router.push('/profile')
      break
    case 'teams':
      router.push('/teams')
      break
    case 'users':
      router.push('/users')
      break
    case 'signout':
      handleLogout()
      break
  }
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// Click outside to close menus
const handleClickOutside = (event) => {
  if (mentionsMenuRef.value && !mentionsMenuRef.value.contains(event.target)) {
    closeMentions()
  }
  if (notificationMenuRef.value && !notificationMenuRef.value.contains(event.target)) {
    closeNotifications()
  }
}

// Fetch notifications when user is available (on load or after auth hydrate) so badge count persists after reload
watch(
  () => authStore.user?.id || authStore.user?.uid,
  (userId) => {
    if (userId) fetchAssignedItems()
  },
  { immediate: true }
)

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  updateNotificationCount()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.chrome-extension-nav-link :deep(.hr-icon-inner),
.chrome-extension-nav-link :deep(.hr-icon-left),
.chrome-extension-nav-link :deep(.hr-icon) {
  position: absolute !important;
  inset: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  border-radius: 50% !important;
  overflow: hidden !important;
}

/* Notification bell - interactive hover & active states, tight padding so badge sits near icon */
.notification-bell-wrap :deep(.notification-bell-btn) {
  border-radius: 0.375rem;
  min-width: 2rem;
  min-height: 2rem;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
}
/* Badge: smaller, top-right aligned to bell icon */
.notification-bell-wrap :deep(.notification-badge) {
  top: -4px;
  right: -14px;
  min-width: 1.125rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.625rem;
  font-weight: 600;
  line-height: 1;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
}
.notification-bell-wrap :deep(.notification-bell-btn:hover) {
  background-color: rgb(243 244 246);
  color: rgb(59 130 246);
}
.dark .notification-bell-wrap :deep(.notification-bell-btn:hover) {
  background-color: rgb(55 65 81);
  color: rgb(96 165 250);
}
.notification-bell-wrap :deep(.notification-bell-btn.notification-bell-open) {
  background-color: rgb(243 244 246);
  color: rgb(59 130 246);
}
.dark .notification-bell-wrap :deep(.notification-bell-btn.notification-bell-open) {
  background-color: rgb(55 65 81);
  color: rgb(96 165 250);
}
.notification-bell-wrap :deep(.notification-bell-btn:active) {
  transform: scale(0.95);
}
.notification-bell-wrap :deep(.notification-bell-icon) {
  transition: transform 0.2s ease;
}
.notification-bell-wrap :deep(.notification-bell-btn:hover .notification-bell-icon) {
  transform: scale(1.08);
}

.chrome-extension-nav-link :deep(button) {
  position: relative !important;
  border-radius: 50% !important;
  width: 2rem !important;
  height: 2rem !important;
  min-width: 2rem !important;
  min-height: 2rem !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 1px solid rgb(209 213 219);
  overflow: hidden !important;
}
.dark .chrome-extension-nav-link :deep(button) {
  border-color: rgb(75 85 99);
}

.chrome-extension-nav-link :deep(.chrome-extension-nav-img) {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;
  margin: 0 !important;
  padding: 0 !important;
  display: block !important;
  border-radius: 50% !important;
}
</style>